import { useCallback, useEffect, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import styled from 'styled-components';
import { Hymn } from '../../../common/src/model.ts';
import { SongOptionsSetting } from '../pages/settings/song_options.tsx';
import { Kebab } from '../util/kebab.tsx';
import './slide_menus.css';
import { SongInfo } from './song_info.tsx';

interface Props {
  show: boolean;
  hymn: Hymn | undefined;
  close: () => void;
  showModal: () => void;
  initiallyExpandedKebabSetting?: SongOptionsSetting;
}

enum Menu {
  Kebab,
  Info,
}

export const SlideMenu = ({ show, hymn, close, showModal, initiallyExpandedKebabSetting }: Props) => {
  const [display, setDisplay] = useState(Menu.Kebab);
  const [landscape, setLandscape] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const handleResize = () => {
      if (window.innerHeight < window.innerWidth) {
        setLandscape(true);
      } else {
        setLandscape(false);
      }
    };
    window.addEventListener('resize', handleResize, { signal: controller.signal });
    return () => controller.abort();
  }, []);

  const displaySongInfo = useCallback(() => {
    setDisplay(Menu.Info);
  }, []);

  const onClickBackground = useCallback(
    (event: any) => {
      close();
      setDisplay(Menu.Kebab);
      event.stopPropagation(); // prevent mixer from closing
    },
    [close],
  );

  const onBack = useCallback(() => {
    setDisplay(Menu.Kebab);
  }, []);

  return (
    <>
      <SlideMenuBackground $show={show} onClick={onClickBackground} />
      <div
        className="slideMenu"
        style={
          show && display === Menu.Kebab
            ? { top: landscape && !isDesktop ? '10%' : '40%', transition: 'top 250ms' }
            : { top: '100%' }
        }
      >
        {hymn && (
          <div className="wrapper">
            <Kebab
              hymn={hymn}
              displaySongInfo={displaySongInfo}
              displayModal={showModal}
              close={close}
              initiallyExpandedSetting={initiallyExpandedKebabSetting}
            />
          </div>
        )}
      </div>
      <div
        className="slideMenu"
        style={
          show && display === Menu.Info
            ? { top: landscape && !isDesktop ? '30%' : '45%', transition: 'top 250ms' }
            : { top: '100%' }
        }
      >
        {hymn && (
          <div className="wrapper">
            <SongInfo back={onBack} hymn={hymn} />
          </div>
        )}
      </div>
    </>
  );
};

const SlideMenuBackground = styled.div<{ $show: boolean }>`
  z-index: ${({ $show }) => ($show ? '190' : '-20')};
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${({ $show }) =>
    $show &&
    `
    background-color: var(--color-background-modal-overlay);
    transition: background-color 200ms ease-in-out;
  `};
`;
